import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckSquare,
  ClipboardList,
  FileText,
  ImagePlus,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  History as HistoryIcon,
  ShieldAlert,
  Printer,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useActiveCompany } from "@/hooks/use-active-company";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { logDocActivity } from "@/lib/project-doc-activity";
import { PreflightCard } from "@/features/electro-docs/PreflightCard";
import { CustomerPackageCard } from "@/features/electro-docs/CustomerPackageCard";
import { HmsTab } from "@/features/electro-docs/HmsTab";
import { KsTab } from "@/features/electro-docs/KsTab";
import { FdvTab } from "@/features/electro-docs/FdvTab";
import {
  useCompleteDocumentation,
  useCustomerComplianceDocuments,
} from "@/features/electro-docs/hooks";
import { sha256Hex } from "@/features/electro-docs/hash";
import { buildGeneralDocumentRow } from "@/features/electro-docs/attachments";
import { projectGeneralStoragePath } from "@/features/electro-docs/storage";

const VALID_TABS = ["overview", "hms", "ks", "fdv", "history"] as const;
type DocTab = (typeof VALID_TABS)[number];

export const Route = createFileRoute("/_authenticated/projects/$id/documentation")({
  validateSearch: (search: Record<string, unknown>): { tab?: DocTab } => {
    const t = search.tab;
    return typeof t === "string" && (VALID_TABS as readonly string[]).includes(t)
      ? { tab: t as DocTab }
      : {};
  },
  component: DocumentationPage,
  head: () => ({
    meta: [
      { title: "Prosjektdokumentasjon | VERQO" },
      {
        name: "description",
        content:
          "HMS, KS og FDV samlet på ett sted – kontrolliste, dokumenter og sporbar utstedelse.",
      },
    ],
  }),
});

const DEFAULT_CHECKLIST = [
  "Arbeidet kontrollert",
  "Dokumentasjon komplett",
  "Bilder lagt ved",
  "Kunde informert",
  "Prosjekt klart for overlevering",
];

function DocumentationPage() {
  const { id: projectId } = Route.useParams();
  const { companyId } = useActiveCompany();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const tab: DocTab = search.tab ?? "overview";
  const setTab = (value: string) => navigate({ search: { tab: value as DocTab }, replace: true });
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const projectQ = useQuery({
    queryKey: ["project-doc", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, name, project_number, module_key, status")
        .eq("id", projectId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Checklist
  const checklistQ = useQuery({
    queryKey: ["project-checklist", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_checklist_items")
        .select("*")
        .eq("project_id", projectId)
        .order("sort_order")
        .order("created_at");
      if (error) throw error;
      return data ?? [];
    },
  });

  const seedChecklist = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error("Ingen aktiv bedrift");
      // Try bedriftens mal først
      const { data: defaults } = await supabase
        .from("company_checklist_defaults")
        .select("label, sort_order")
        .eq("company_id", companyId)
        .order("sort_order");
      const labels =
        defaults && defaults.length > 0 ? defaults.map((d) => d.label) : DEFAULT_CHECKLIST;
      const rows = labels.map((label, i) => ({
        project_id: projectId,
        company_id: companyId,
        label,
        sort_order: i,
      }));
      const { error } = await supabase.from("project_checklist_items").insert(rows);
      if (error) throw error;
      await logDocActivity({
        projectId,
        companyId,
        kind: "checklist_created",
        summary: `Kontrolliste opprettet (${labels.length} punkter)`,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-checklist", projectId] });
      qc.invalidateQueries({ queryKey: ["project-doc-activity", projectId] });
      toast.success("Kontrolliste opprettet");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Feilet"),
  });

  const toggleItem = useMutation({
    mutationFn: async (input: { id: string; checked: boolean; label: string }) => {
      const { data: userRes } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("project_checklist_items")
        .update({
          checked: input.checked,
          checked_at: input.checked ? new Date().toISOString() : null,
          checked_by: input.checked ? (userRes.user?.id ?? null) : null,
        })
        .eq("id", input.id);
      if (error) throw error;
      if (companyId) {
        await logDocActivity({
          projectId,
          companyId,
          kind: input.checked ? "checklist_checked" : "checklist_unchecked",
          summary: `${input.checked ? "Krysset av" : "Fjernet kryss"}: ${input.label}`,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-checklist", projectId] });
      qc.invalidateQueries({ queryKey: ["project-doc-activity", projectId] });
    },
  });

  const [newItem, setNewItem] = useState("");
  const addItem = useMutation({
    mutationFn: async () => {
      const label = newItem.trim();
      if (!label || !companyId) return;
      const nextOrder = (checklistQ.data ?? []).length;
      const { error } = await supabase.from("project_checklist_items").insert({
        project_id: projectId,
        company_id: companyId,
        label,
        sort_order: nextOrder,
      });
      if (error) throw error;
      await logDocActivity({
        projectId,
        companyId,
        kind: "checklist_updated",
        summary: `La til kontrollpunkt: ${label}`,
      });
      setNewItem("");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-checklist", projectId] });
      qc.invalidateQueries({ queryKey: ["project-doc-activity", projectId] });
    },
  });

  const removeItem = useMutation({
    mutationFn: async (input: { id: string; label: string }) => {
      const { error } = await supabase.from("project_checklist_items").delete().eq("id", input.id);
      if (error) throw error;
      if (companyId) {
        await logDocActivity({
          projectId,
          companyId,
          kind: "checklist_updated",
          summary: `Fjernet kontrollpunkt: ${input.label}`,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-checklist", projectId] });
      qc.invalidateQueries({ queryKey: ["project-doc-activity", projectId] });
    },
  });

  // Documents (notes + files)
  const documentsQ = useQuery({
    queryKey: ["project-documents", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_documents")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const [newDocTitle, setNewDocTitle] = useState("");
  const [newDocContent, setNewDocContent] = useState("");
  const addNote = useMutation({
    mutationFn: async () => {
      const title = newDocTitle.trim();
      const content = newDocContent.trim();
      if (!title || !companyId) return;
      const { data: userRes } = await supabase.auth.getUser();
      const { error } = await supabase.from("project_documents").insert({
        project_id: projectId,
        company_id: companyId,
        kind: "note",
        title,
        content,
        created_by: userRes.user?.id ?? null,
      });
      if (error) throw error;
      await logDocActivity({
        projectId,
        companyId,
        kind: "document_added",
        summary: `Notat opprettet: ${title}`,
      });
      setNewDocTitle("");
      setNewDocContent("");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-documents", projectId] });
      qc.invalidateQueries({ queryKey: ["project-doc-activity", projectId] });
      toast.success("Notat lagret");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Feilet"),
  });

  const removeDoc = useMutation({
    mutationFn: async (input: { id: string; title: string; storage_path: string | null }) => {
      if (input.storage_path) {
        await supabase.storage.from("project-images").remove([input.storage_path]);
      }
      const { error } = await supabase.from("project_documents").delete().eq("id", input.id);
      if (error) throw error;
      if (companyId) {
        await logDocActivity({
          projectId,
          companyId,
          kind: "document_deleted",
          summary: `Slettet: ${input.title}`,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-documents", projectId] });
      qc.invalidateQueries({ queryKey: ["project-doc-activity", projectId] });
    },
  });

  const uploadFile = useMutation({
    mutationFn: async (file: File) => {
      if (!companyId) throw new Error("Ingen aktiv bedrift");
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id;
      if (!uid) throw new Error("Ikke innlogget");
      // Storage-policy krever companyId/projectId/general/<fil> for generelle filer.
      const path = projectGeneralStoragePath({
        companyId,
        projectId,
        fileName: file.name,
        uuid: crypto.randomUUID(),
      });
      const sha256 = await sha256Hex(file);
      const { error: upErr } = await supabase.storage
        .from("project-images")
        .upload(path, file, { contentType: file.type });
      if (upErr) throw upErr;
      const row = buildGeneralDocumentRow({
        projectId,
        companyId,
        file,
        storagePath: path,
        sha256,
        createdBy: uid,
      });
      const kind = row.kind;
      const { error: insErr } = await supabase.from("project_documents").insert(row);
      if (insErr) {
        // Rydd bort foreldreløs fil før feilen vises.
        await supabase.storage.from("project-images").remove([path]);
        throw insErr;
      }
      await logDocActivity({
        projectId,
        companyId,
        kind: kind === "image" ? "image_added" : "document_added",
        summary: `${kind === "image" ? "Bilde" : "Vedlegg"} lastet opp: ${file.name}`,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-documents", projectId] });
      qc.invalidateQueries({ queryKey: ["project-doc-activity", projectId] });
      toast.success("Fil lastet opp");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Feilet"),
  });

  const onPickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadingImage(true);
    try {
      for (const f of Array.from(files)) await uploadFile.mutateAsync(f);
    } finally {
      setUploadingImage(false);
    }
  };

  // Closure
  const closureQ = useQuery({
    queryKey: ["project-closure", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_closures")
        .select("*")
        .eq("project_id", projectId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const closure = closureQ.data;
  const [summary, setSummary] = useState<string | null>(null);
  const [handover, setHandover] = useState<string | null>(null);
  const [samsvar, setSamsvar] = useState<string | null>(null);
  const summaryValue = summary ?? closure?.summary ?? "";
  const handoverValue = handover ?? closure?.handover_text ?? "";
  const samsvarValue = samsvar ?? closure?.samsvar_draft ?? "";

  const upsertClosure = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      if (!companyId) throw new Error("Ingen aktiv bedrift");
      if (closure?.id) {
        const { error } = await supabase
          .from("project_closures")
          .update(patch as never)
          .eq("id", closure.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("project_closures").insert({
          project_id: projectId,
          company_id: companyId,
          ...(patch as Record<string, unknown>),
        } as never);
        if (error) throw error;
      }
      await logDocActivity({
        projectId,
        companyId,
        kind: "closure_updated",
        summary: "Avslutningsdata oppdatert",
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-closure", projectId] });
      qc.invalidateQueries({ queryKey: ["project-doc-activity", projectId] });
      toast.success("Lagret");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Feilet"),
  });

  const completeClosure = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error("Ingen aktiv bedrift");
      const { data: userRes } = await supabase.auth.getUser();
      const payload = {
        status: "completed",
        summary: summaryValue,
        handover_text: handoverValue,
        samsvar_draft: samsvarValue,
        closed_at: new Date().toISOString(),
        closed_by: userRes.user?.id ?? null,
      };
      if (closure?.id) {
        const { error } = await supabase
          .from("project_closures")
          .update(payload)
          .eq("id", closure.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("project_closures").insert({
          project_id: projectId,
          company_id: companyId,
          ...payload,
        });
        if (error) throw error;
      }
      await logDocActivity({
        projectId,
        companyId,
        kind: "closure_completed",
        summary: "Prosjektet markert som avsluttet",
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-closure", projectId] });
      qc.invalidateQueries({ queryKey: ["project-doc-activity", projectId] });
      toast.success("Prosjekt avsluttet");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Feilet"),
  });

  // Activity
  const activityQ = useQuery({
    queryKey: ["project-doc-activity", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_doc_activity")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false })
        .limit(60);
      if (error) throw error;
      return data ?? [];
    },
  });

  const project = projectQ.data;
  const isElectro = project?.module_key === "electrician";
  const customerDocsQ = useCustomerComplianceDocuments(projectId);
  const hasIssuedCustomerDocs = (customerDocsQ.data ?? []).length > 0;
  const [acceptWarnings, setAcceptWarnings] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const { complete, reopen } = useCompleteDocumentation(projectId);
  const checklistItems = checklistQ.data ?? [];
  const documents = documentsQ.data ?? [];
  const activities = activityQ.data ?? [];
  const checklistComplete = useMemo(
    () => checklistItems.length > 0 && checklistItems.every((c) => c.checked),
    [checklistItems],
  );

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 lg:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/projects/$id" params={{ id: projectId }}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Tilbake til prosjekt
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          {isElectro && !hasIssuedCustomerDocs ? (
            <Button size="sm" variant="outline" disabled title="Ingen utstedte dokumenter">
              <Printer className="mr-1 h-4 w-4" /> Eksporter PDF
            </Button>
          ) : (
            <Button asChild size="sm" variant="outline">
              <Link to="/projects/$id/documentation/pdf" params={{ id: projectId }}>
                <Printer className="mr-1 h-4 w-4" /> Eksporter PDF
              </Link>
            </Button>
          )}
        </div>
      </div>
      {isElectro && !hasIssuedCustomerDocs && (
        <p className="text-xs text-muted-foreground">
          Kunde-PDF kan først eksporteres når minst ett dokument er utstedt og låst.
        </p>
      )}

      <Card className="p-4">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
            <ClipboardList className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold tracking-tight">Dokumentasjon</h1>
            <div className="text-xs text-muted-foreground">
              {project?.name ?? "…"} · {project?.project_number ?? "—"} ·{" "}
              {project?.status ?? "aktivt"}
            </div>
          </div>
        </div>
      </Card>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
        <div className="flex items-start gap-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            VERQO hjelper deg å samle dokumentasjon på ett sted. Systemet erstatter ikke lovpålagte
            krav eller autorisert faglig kontroll. Alle utkast må kontrolleres og godkjennes av
            ansvarlig person.
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full flex-wrap justify-start">
          <TabsTrigger value="overview">
            <CheckSquare className="mr-1 h-4 w-4" /> Oversikt
          </TabsTrigger>
          <TabsTrigger value="hms">
            <ShieldAlert className="mr-1 h-4 w-4" /> HMS
          </TabsTrigger>
          <TabsTrigger value="ks">
            <Sparkles className="mr-1 h-4 w-4" /> KS
          </TabsTrigger>
          <TabsTrigger value="fdv">
            <FileText className="mr-1 h-4 w-4" /> FDV
          </TabsTrigger>
          <TabsTrigger value="history">
            <HistoryIcon className="mr-1 h-4 w-4" /> Historikk
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          {isElectro && <PreflightCard projectId={projectId} />}
          {isElectro && <CustomerPackageCard projectId={projectId} />}
          <Card className="p-4">
            {checklistItems.length === 0 ? (
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  Ingen kontrolliste opprettet ennå. Start fra bedriftens mal eller en generell mal.
                </div>
                <Button
                  size="sm"
                  onClick={() => seedChecklist.mutate()}
                  disabled={seedChecklist.isPending}
                >
                  {seedChecklist.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="mr-1 h-4 w-4" />
                  )}
                  Opprett kontrolliste
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <ul className="divide-y divide-border">
                  {checklistItems.map((it) => (
                    <li key={it.id} className="flex items-center gap-3 py-2">
                      <Checkbox
                        checked={it.checked}
                        onCheckedChange={(v) =>
                          toggleItem.mutate({ id: it.id, checked: Boolean(v), label: it.label })
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <div
                          className={`text-sm ${it.checked ? "line-through text-muted-foreground" : ""}`}
                        >
                          {it.label}
                        </div>
                        {it.checked_at && (
                          <div className="text-xs text-muted-foreground">
                            Kontrollert {new Date(it.checked_at).toLocaleString("nb-NO")}
                          </div>
                        )}
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeItem.mutate({ id: it.id, label: it.label })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2 pt-2">
                  <Input
                    placeholder="Nytt kontrollpunkt…"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addItem.mutate();
                    }}
                  />
                  <Button size="sm" onClick={() => addItem.mutate()} disabled={!newItem.trim()}>
                    <Plus className="mr-1 h-4 w-4" /> Legg til
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground pt-1">
                  {checklistItems.filter((c) => c.checked).length} av {checklistItems.length}{" "}
                  kontrollert
                  {checklistComplete && " ✓"}
                </div>
              </div>
            )}
          </Card>

          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold">Filer og bilder</div>
              <div>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  accept="image/*,application/pdf,.doc,.docx,.txt"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => onPickFiles(e.target.files)}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInput.current?.click()}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <ImagePlus className="mr-1 h-4 w-4" />
                  )}
                  Last opp / ta bilde
                </Button>
              </div>
            </div>
            {documents.filter((d) => d.storage_path).length === 0 ? (
              <div className="text-sm text-muted-foreground">Ingen filer lastet opp ennå.</div>
            ) : (
              <ul className="divide-y divide-border">
                {documents
                  .filter((d) => d.storage_path)
                  .map((d) => (
                    <li key={d.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{d.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {d.kind} · {new Date(d.created_at).toLocaleString("nb-NO")}
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() =>
                          removeDoc.mutate({
                            id: d.id,
                            title: d.title,
                            storage_path: d.storage_path,
                          })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
              </ul>
            )}
          </Card>

          <Card className="p-4">
            <div className="mb-3 text-sm font-semibold">Notater</div>
            <div className="space-y-2">
              <Input
                placeholder="Tittel"
                value={newDocTitle}
                onChange={(e) => setNewDocTitle(e.target.value)}
              />
              <Textarea
                placeholder="Innhold (valgfritt)…"
                value={newDocContent}
                onChange={(e) => setNewDocContent(e.target.value)}
                rows={3}
              />
              <div>
                <Button
                  size="sm"
                  onClick={() => addNote.mutate()}
                  disabled={!newDocTitle.trim() || addNote.isPending}
                >
                  <Upload className="mr-1 h-4 w-4" /> Lagre notat
                </Button>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {documents
                .filter((d) => !d.storage_path)
                .map((d) => (
                  <div key={d.id} className="rounded border border-border p-3 text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{d.title}</div>
                        {d.content && (
                          <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">
                            {d.content}
                          </p>
                        )}
                        <div className="mt-1 text-xs text-muted-foreground">
                          {new Date(d.created_at).toLocaleString("nb-NO")}
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() =>
                          removeDoc.mutate({ id: d.id, title: d.title, storage_path: null })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          </Card>

          <Card className="p-4">
            <SectionHeader title="Oppsummering av utført arbeid" />
            <Textarea
              value={summaryValue}
              onChange={(e) => setSummary(e.target.value)}
              rows={6}
              placeholder="Beskriv kort hva som er utført…"
            />
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                onClick={() => upsertClosure.mutate({ summary: summaryValue })}
                disabled={upsertClosure.isPending}
              >
                Lagre oppsummering
              </Button>
            </div>
          </Card>

          <Card className="p-4">
            <SectionHeader title="Overleveringstekst til kunde" />
            <Textarea
              value={handoverValue}
              onChange={(e) => setHandover(e.target.value)}
              rows={6}
              placeholder="Skriv en overleveringstekst…"
            />
            <div className="mt-2 flex justify-end">
              <Button
                size="sm"
                onClick={() => upsertClosure.mutate({ handover_text: handoverValue })}
                disabled={upsertClosure.isPending}
              >
                Lagre overleveringstekst
              </Button>
            </div>
          </Card>

          {isElectro ? (
            samsvarValue.trim() ? (
              <Card className="p-4">
                <SectionHeader title="Historisk samsvarsutkast (read-only)" />
                <div className="mb-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                  Dette utkastet er fra den gamle flyten og er ikke lenger et gyldig
                  samsvarsdokument. Samsvarserklæring utstedes nå under KS.
                </div>
                <p className="whitespace-pre-wrap text-xs text-muted-foreground">{samsvarValue}</p>
              </Card>
            ) : null
          ) : (
            <Card className="p-4">
              <SectionHeader title="Utkast til samsvarsdokument" />
              <div className="mb-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                Dette er et utkast. Innholdet må kontrolleres og signeres av ansvarlig person før
                bruk.
              </div>
              <Textarea
                value={samsvarValue}
                onChange={(e) => setSamsvar(e.target.value)}
                rows={10}
                placeholder="Utkast til dokumentasjonstekst…"
              />
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  onClick={() => upsertClosure.mutate({ samsvar_draft: samsvarValue })}
                  disabled={upsertClosure.isPending}
                >
                  Lagre utkast
                </Button>
              </div>
            </Card>
          )}

          <Card className="p-4">
            <div className="mb-2 text-sm font-semibold">Fullfør dokumentasjonen</div>
            {isElectro ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Se den faktiske Kundepakke-kontrollen ovenfor for leveransestatus. Fullføring
                  kjører serverens dokumentasjonskontroll; røde juridiske mangler blokkeres fortsatt
                  av serverpreflight til de er rettet.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox
                      checked={acceptWarnings}
                      onCheckedChange={(v) => setAcceptWarnings(Boolean(v))}
                    />
                    Godta gule advarsler
                  </label>
                  <Button
                    size="sm"
                    disabled={complete.isPending}
                    onClick={() =>
                      complete.mutate({
                        summary: summaryValue,
                        handoverText: handoverValue,
                        acceptWarnings,
                      })
                    }
                  >
                    {complete.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    Fullfør og lås
                  </Button>
                  {closure?.status === "completed" && (
                    <>
                      <Input
                        className="max-w-xs"
                        placeholder="Begrunnelse for gjenåpning"
                        value={reopenReason}
                        onChange={(e) => setReopenReason(e.target.value)}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!reopenReason.trim() || reopen.isPending}
                        onClick={() => reopen.mutate(reopenReason.trim())}
                      >
                        Gjenåpne
                      </Button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  Marker prosjektet som avsluttet. Du kan fortsatt endre dokumentasjonen etterpå.
                </p>
                <div className="mt-3">
                  <Button
                    size="sm"
                    onClick={() => completeClosure.mutate()}
                    disabled={completeClosure.isPending}
                  >
                    {completeClosure.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    {closure?.status === "completed" ? "Oppdater avslutning" : "Fullfør prosjekt"}
                  </Button>
                </div>
              </>
            )}
            {closure?.closed_at && (
              <div className="mt-2 text-xs text-muted-foreground">
                Avsluttet {new Date(closure.closed_at).toLocaleString("nb-NO")}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="hms" className="mt-4">
          {isElectro && companyId ? (
            <HmsTab projectId={projectId} companyId={companyId} />
          ) : (
            <AreaUnavailable />
          )}
        </TabsContent>

        <TabsContent value="ks" className="mt-4">
          {isElectro && companyId ? (
            <KsTab projectId={projectId} companyId={companyId} />
          ) : (
            <AreaUnavailable />
          )}
        </TabsContent>

        <TabsContent value="fdv" className="mt-4">
          {isElectro && companyId ? (
            <FdvTab projectId={projectId} companyId={companyId} />
          ) : (
            <AreaUnavailable />
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="p-4">
            {activities.length === 0 ? (
              <div className="text-sm text-muted-foreground">Ingen hendelser ennå.</div>
            ) : (
              <ol className="space-y-2">
                {activities.map((ev) => (
                  <li key={ev.id} className="flex items-start gap-3 text-sm">
                    <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{ev.summary}</div>
                      <div className="text-xs text-muted-foreground">
                        {ev.kind} · {new Date(ev.created_at).toLocaleString("nb-NO")}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AreaUnavailable() {
  return (
    <Card className="p-6 text-sm text-muted-foreground">
      Dette arbeidsområdet er foreløpig kun tilgjengelig for prosjekter i elektrikermodulen.
    </Card>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="mb-2 flex items-center justify-between">
      <div className="text-sm font-semibold">{title}</div>
    </div>
  );
}
